// Tenses & Moods — a complete reference for each Italian tense with uses,
// the parlare pattern, example sentences, and a tip. Ported from the legacy
// views.tenses; the `sections` data and markup are unchanged, only the
// `(app)` mount-element signature and a smooth scroll-to-top were added.
import { speakButtonHTML } from "../lib/audio.js";

const SECTIONS = [
  {
    id: "presente",
    mood: "Indicative",
    title: "Presente (Present)",
    icon: "☀️",
    color: "bg-sage-50 border-sage-200",
    uses: "Current actions, habitual actions, general truths, and near-future plans.",
    examples: [
      { es: "Parlo italiano tutti i giorni.", en: "I speak Italian every day." },
      { es: "Il sole sorge a est.", en: "The sun rises in the east." },
      { es: "Domani vado in palestra.", en: "Tomorrow I'm going to the gym." },
      { es: "Cosa fai?", en: "What are you doing?" },
    ],
    pattern: "parlo · parli · parla · parliamo · parlate · parlano",
    tip: "Italian present covers both English \"I speak\" and \"I am speaking\". For something happening right this moment you can use stare + gerundio (sto parlando), but the simple present usually does the job.",
  },
  {
    id: "preterito",
    mood: "Indicative",
    title: "Passato Prossimo (Present Perfect / Past)",
    icon: "📅",
    color: "bg-sand-50 border-sand-200",
    uses: "The everyday past — completed actions, whether minutes or years ago. In speech it covers what English splits between \"I ate\" and \"I have eaten\".",
    examples: [
      { es: "Ieri ho mangiato la pizza.", en: "Yesterday I ate pizza." },
      { es: "Siamo arrivati alle otto.", en: "We arrived at eight." },
      { es: "L'anno scorso ho viaggiato in Messico.", en: "Last year I traveled to Mexico." },
      { es: "Hai mai mangiato il sushi?", en: "Have you ever eaten sushi?" },
    ],
    pattern: "avere/essere (present) + participio: ho/hai/ha/abbiamo/avete/hanno + parlato",
    tip: "Pick the auxiliary: most verbs take avere; verbs of motion and change of state (andare, venire, nascere, diventare) and all reflexives take essere — and with essere the participle agrees: lei è andata, loro sono arrivati.",
  },
  {
    id: "imperfecto",
    mood: "Indicative",
    title: "Imperfetto (Imperfect)",
    icon: "🎞️",
    color: "bg-sage-50 border-sage-200",
    uses: "Ongoing, repeated, or background actions in the past — the scenery, not the events.",
    examples: [
      { es: "Quando ero bambino, giocavo a calcio.", en: "When I was a child, I used to play soccer." },
      { es: "Pioveva mentre camminavamo.", en: "It was raining while we were walking." },
      { es: "Mia nonna cucinava ogni domenica.", en: "My grandmother used to cook every Sunday." },
      { es: "Erano le tre del pomeriggio.", en: "It was three in the afternoon." },
    ],
    pattern: "parlavo · parlavi · parlava · parlavamo · parlavate · parlavano",
    tip: "Used for \"used to\", \"was -ing\", descriptions, age, time, and weather in the past. Only essere is truly irregular (ero, eri, era…); fare, dire and bere use a fuller stem (facevo, dicevo, bevevo).",
  },
  {
    id: "preterito-vs-imperfecto",
    mood: "Contrast",
    title: "Passato Prossimo vs Imperfetto",
    icon: "⚖️",
    color: "bg-amber-50 border-amber-200",
    uses: "The hardest contrast for English speakers. Both translate as English past tense, but they tell different stories.",
    examples: [
      { es: "Mangiavo quando hai chiamato.", en: "I was eating when you called. (background + event)" },
      { es: "Ho mangiato quando hai chiamato.", en: "I ate when you called. (two events in sequence)" },
      { es: "Ho conosciuto Maria nel 2010.", en: "I met María in 2010. (one moment)" },
      { es: "Conoscevo Maria.", en: "I knew/used to know María. (ongoing state)" },
    ],
    pattern: "Passato prossimo = WHAT happened · Imperfetto = WHAT WAS HAPPENING / HOW it was",
    tip: "If you can rewrite it in English as \"was -ing\" or \"used to\", it's almost always imperfetto. A single completed event is passato prossimo.",
  },
  {
    id: "futuro",
    mood: "Indicative",
    title: "Futuro Semplice (Future)",
    icon: "🚀",
    color: "bg-sage-50 border-sage-200",
    uses: "Predictions, promises, and probability/conjecture in the present.",
    examples: [
      { es: "Domani parlerò con lui.", en: "Tomorrow I will speak with him." },
      { es: "Non dimenticheremo mai questo giorno.", en: "We will never forget this day." },
      { es: "Dove sarà Maria?", en: "I wonder where María is. (probability)" },
      { es: "Saranno le dieci.", en: "It must be about ten o'clock." },
    ],
    pattern: "parlerò · parlerai · parlerà · parleremo · parlerete · parleranno",
    tip: "In casual speech Italians often use the present for the near future: domani parto (I'm leaving tomorrow). Several verbs have contracted stems: andare → andrò, avere → avrò, essere → sarò.",
  },
  {
    id: "condicional",
    mood: "Conditional",
    title: "Condizionale Presente (Conditional)",
    icon: "🤔",
    color: "bg-sage-50 border-sage-200",
    uses: "Polite requests, hypothetical situations, the \"would\" of English.",
    examples: [
      { es: "Vorrei un caffè.", en: "I would like a coffee." },
      { es: "Potresti aiutarmi?", en: "Could you help me?" },
      { es: "Al tuo posto, non andrei.", en: "If I were you, I wouldn't go." },
      { es: "Ha detto che sarebbe arrivato tardi.", en: "He said he would arrive late." },
    ],
    pattern: "parlerei · parleresti · parlerebbe · parleremmo · parlereste · parlerebbero",
    tip: "It shares the future's stem (andare → andr-) but takes conditional endings (-ei, -esti, -ebbe, -emmo, -este, -ebbero). For \"would have\", use the condizionale passato: avrei parlato, sarei andato.",
  },
  {
    id: "presente-subjuntivo",
    mood: "Subjunctive",
    title: "Congiuntivo Presente (Present Subjunctive)",
    icon: "💭",
    color: "bg-violet-50 border-violet-200",
    uses: "Doubt, desire, emotion, opinion — anything introduced by che that isn't a stated fact.",
    examples: [
      { es: "Voglio che tu venga.", en: "I want you to come." },
      { es: "È importante che tu studi.", en: "It's important that you study." },
      { es: "Penso che sia vero.", en: "I think it's true." },
      { es: "Spero che tu abbia una buona giornata.", en: "I hope you have a good day." },
      { es: "Benché sia tardi, esco.", en: "Although it's late, I'm going out." },
    ],
    pattern: "(che io) parli · parli · parli · parliamo · parliate · parlino",
    tip: "Triggered after che by verbs of opinion, wish, doubt and emotion (penso che, voglio che, dubito che, sono felice che) and by conjunctions like benché, affinché, prima che. The three singular forms are identical, so keep the pronoun for clarity.",
  },
  {
    id: "imperfecto-subjuntivo",
    mood: "Subjunctive",
    title: "Congiuntivo Imperfetto (Imperfect Subjunctive)",
    icon: "🌙",
    color: "bg-violet-50 border-violet-200",
    uses: "Past-tense subjunctive: hypothetical 'if' clauses and congiuntivo triggered by a past or conditional main clause.",
    examples: [
      { es: "Se avessi soldi, viaggerei.", en: "If I had money, I would travel." },
      { es: "Volevo che tu venissi.", en: "I wanted you to come." },
      { es: "Era importante che arrivassi in tempo.", en: "It was important that you arrived on time." },
      { es: "Vorrei che tu fossi qui.", en: "I wish you were here." },
    ],
    pattern: "(che io) parlassi · parlassi · parlasse · parlassimo · parlaste · parlassero",
    tip: "The backbone of unreal 'if' sentences: se + congiuntivo imperfetto, then condizionale (Se potessi, lo farei). Essere is irregular: fossi, fossi, fosse, fossimo, foste, fossero.",
  },
  {
    id: "imperativo",
    mood: "Imperative",
    title: "Imperativo (Commands)",
    icon: "❗",
    color: "bg-rose-50 border-rose-200",
    uses: "Direct commands and instructions.",
    examples: [
      { es: "Parla più piano!", en: "Speak more slowly!" },
      { es: "Non parlare così in fretta!", en: "Don't speak so fast!" },
      { es: "Mangiamo adesso.", en: "Let's eat now." },
      { es: "Si accomodi, prego.", en: "Please take a seat. (formal)" },
    ],
    pattern: "(tu) parla · (Lei) parli · (noi) parliamo · (voi) parlate",
    tip: "Two traps: the negative tu command is non + the infinitive (Non parlare!), and the formal Lei command uses the congiuntivo (parli, scriva). Pronouns attach to informal commands (dimmi!) but come before the formal one (mi dica).",
  },
  {
    id: "perfecto",
    mood: "Indicative",
    title: "Passato Remoto (Historic Past)",
    icon: "📜",
    color: "bg-sand-50 border-sand-200",
    uses: "A one-word past for completed actions felt as distant or finished — the tense of novels, history, and fairy tales.",
    examples: [
      { es: "Dante nacque nel 1265.", en: "Dante was born in 1265." },
      { es: "Andammo a Roma e visitammo il Colosseo.", en: "We went to Rome and visited the Colosseum." },
      { es: "Lei aprì la porta e uscì.", en: "She opened the door and went out." },
    ],
    pattern: "parlai · parlasti · parlò · parlammo · parlaste · parlarono",
    tip: "In the north it's mostly replaced by the passato prossimo in speech, but it thrives in the south and in all written narrative. Many -ere verbs are irregular in a \"1-3-3\" way (io/lui/loro): prendere → presi, prese, presero.",
  },
  {
    id: "pluscuamperfecto",
    mood: "Compound",
    title: "Trapassato Prossimo (Past Perfect)",
    icon: "⏪",
    color: "bg-sand-50 border-sand-200",
    uses: "An action that happened BEFORE another past action — \"had done\" in English.",
    examples: [
      { es: "Quando sono arrivato, avevano già mangiato.", en: "When I arrived, they had already eaten." },
      { es: "Non avevo mai visto una cosa simile.", en: "I had never seen anything like that." },
      { es: "Mi disse che aveva finito.", en: "He told me he had finished." },
    ],
    pattern: "avere/essere (imperfect) + participio: avevo/avevi/aveva… + parlato",
    tip: "Always combined with another past tense to show one event came first. Built like the passato prossimo but with the auxiliary in the imperfetto (avevo / ero). The English shortcut: if you'd say \"had\" + past participle, use trapassato.",
  },
];

export function tensesView(app) {
  const tocItems = SECTIONS.map(
    (s) => `
    <a href="#${s.id}" class="block text-sm text-sage-600 hover:text-sage-800 py-1">
      ${s.icon} ${s.title}
    </a>`
  ).join("");

  const sectionHTML = SECTIONS.map(
    (s) => `
    <article id="${s.id}" class="${s.color} border rounded-2xl p-5 scroll-mt-32">
      <div class="flex items-start justify-between gap-3 flex-wrap mb-2">
        <h3 class="font-bold text-lg text-sage-800">${s.icon} ${s.title}</h3>
        <span class="text-xs px-2 py-1 rounded-full bg-white/70 text-sage-600 font-medium">${s.mood}</span>
      </div>
      <p class="text-sm text-sage-700 mb-3"><b>When to use:</b> ${s.uses}</p>
      <div class="bg-white/60 rounded-lg p-3 mb-3">
        <p class="text-xs uppercase tracking-wide text-sage-500 mb-1">Pattern (parlare)</p>
        <p class="font-mono text-xs text-sage-700 leading-relaxed">${s.pattern}</p>
      </div>
      <div class="space-y-2 mb-3">
        <p class="text-xs uppercase tracking-wide text-sage-500">Examples</p>
        ${s.examples
          .map(
            (ex) => `
          <div class="bg-white/60 rounded-lg p-2.5 text-sm">
            <p class="text-sage-800 flex items-start gap-1.5"><b>${ex.es}</b>${speakButtonHTML(ex.es)}</p>
            <p class="text-sage-500 text-xs italic mt-0.5">${ex.en}</p>
          </div>`
          )
          .join("")}
      </div>
      <p class="text-xs text-sage-600 leading-relaxed bg-white/40 rounded-lg p-2.5">💡 ${s.tip}</p>
    </article>
  `
  ).join("");

  app.innerHTML = `
    <section>
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-sage-800 mb-1">Tenses & Moods</h2>
        <p class="text-sage-600 text-sm">A complete guide to Italian verb tenses — when to use each, with example sentences.</p>
      </div>

      <div class="grid lg:grid-cols-[220px_1fr] gap-6">
        <aside class="bg-white rounded-2xl border border-sage-100 p-4 self-start lg:sticky lg:top-4">
          <p class="text-xs uppercase tracking-wide text-sage-500 font-medium mb-2">Jump to</p>
          <nav>${tocItems}</nav>
        </aside>

        <div class="space-y-4">
          <article class="bg-gradient-to-br from-sage-50 to-sand-50 border border-sage-100 rounded-2xl p-5">
            <h3 class="font-bold text-sage-800 mb-2">🗺️ The big picture</h3>
            <p class="text-sm text-sage-700 mb-2">Italian verbs encode <b>three moods</b> plus the conditional, and many tenses:</p>
            <ul class="text-sm text-sage-700 space-y-1.5 list-disc pl-5">
              <li><b>Indicative</b> — states facts and reality: <i>presente, passato prossimo, imperfetto, passato remoto, futuro</i>.</li>
              <li><b>Subjunctive (congiuntivo)</b> — doubt, desire, emotion, opinion: <i>congiuntivo presente, congiuntivo imperfetto</i>.</li>
              <li><b>Imperative</b> — gives commands: <i>parla, non parlare</i>.</li>
            </ul>
            <p class="text-sm text-sage-700 mt-2">The <b>condizionale</b> ("would") and the <b>compound tenses</b> round it out — compounds pair <i>essere</i> or <i>avere</i> with a past participle: <i>ho parlato</i>, <i>avevo parlato</i>, <i>avrei parlato</i>.</p>
          </article>

          ${sectionHTML}

          <article class="bg-white rounded-2xl border border-sage-100 p-5">
            <h3 class="font-bold text-sage-800 mb-3">🧭 Choosing the right tense — quick decision tree</h3>
            <div class="text-sm text-sage-700 space-y-2">
              <p><b>Is the action happening now or generally true?</b> → Presente</p>
              <p><b>Did it happen and is it now over?</b> → Passato Prossimo (or Passato Remoto in writing)</p>
              <p><b>Was it ongoing, repeated, or descriptive in the past?</b> → Imperfetto</p>
              <p><b>Will it happen?</b> → Futuro (or the present in casual speech)</p>
              <p><b>Would you do it?</b> → Condizionale</p>
              <p><b>Does the sentence express a wish, doubt, emotion, or opinion after "che"?</b> → Congiuntivo</p>
              <p><b>Are you giving a command?</b> → Imperativo</p>
              <p><b>"Had done"?</b> → Trapassato (compound tense with <i>essere</i> / <i>avere</i>)</p>
            </div>
          </article>
        </div>
      </div>
    </section>
  `;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
